import { useState } from 'react';
import { Alert, App, Button, Card, Skeleton, Space, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { membershipApi } from '../api/membershipApi';
import { MembershipForm } from '../components/MembershipForm';
import { useMembership } from '../hooks/useMembership';
import { membershipErrorMessage } from '../utils/errors';
import {
  membershipToFormValues,
  toUpdateMembershipBody,
  type MembershipFormValues,
} from '../utils/membershipForm';

/**
 * Página de edición de Membresía (`/membresias/:membershipId/editar`, solo
 * `SUPER_ADMIN`). Carga el detalle con `useMembership` y lo mapea al formulario.
 * El PATCH permite editar todos los campos, incluido el ciclo y el estado.
 */
export function EditMembershipPage() {
  const { membershipId } = useParams<{ membershipId: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { membership, loading, error, notFound, forbidden, refresh } =
    useMembership(membershipId);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const goToList = () => navigate('/membresias');
  const goToDetail = () =>
    membershipId
      ? navigate(`/membresias/${encodeURIComponent(membershipId)}`)
      : goToList();

  const handleSubmit = async (values: MembershipFormValues) => {
    if (!membershipId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await membershipApi.update(membershipId, toUpdateMembershipBody(values));
      message.success('Membresía actualizada.');
      goToDetail();
    } catch (caught) {
      setSubmitError(
        membershipErrorMessage(caught, 'No se pudo actualizar la membresía.'),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="membresias-page">
      <div className="membresias-page__header">
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al detalle de la membresía"
            onClick={goToDetail}
          >
            Volver
          </Button>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Editar membresía
          </Typography.Title>
        </Space>
      </div>

      {loading ? (
        <Card>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      ) : notFound ? (
        <Alert
          type="warning"
          showIcon
          message="No encontramos la membresía"
          description="Puede que se haya eliminado o que el enlace esté incompleto."
          action={
            <Button size="small" onClick={goToList}>
              Volver al listado
            </Button>
          }
        />
      ) : forbidden ? (
        <Alert
          type="warning"
          showIcon
          message="Sin acceso a esta membresía"
          description="No tienes permisos para consultar esta membresía inactiva. Inicia sesión con un rol administrativo."
          action={
            <Button size="small" onClick={goToList}>
              Volver al listado
            </Button>
          }
        />
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar la membresía"
          description={error}
          action={
            <Button size="small" onClick={refresh}>
              Reintentar
            </Button>
          }
        />
      ) : membership ? (
        <>
          {submitError && (
            <Alert
              type="error"
              showIcon
              role="alert"
              message="No se pudo actualizar la membresía"
              description={submitError}
            />
          )}
          <Card>
            <MembershipForm
              mode="edit"
              initialValues={membershipToFormValues(membership)}
              submitting={submitting}
              onSubmit={handleSubmit}
              onCancel={goToDetail}
            />
          </Card>
        </>
      ) : null}
    </div>
  );
}
